import {
  Button,
  ButtonProps,
  cn,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@heroui/react";
import { t } from "@lingui/core/macro";
import { useMediaQuery } from "@mantine/hooks";
import { ArrowLeft, Ellipsis, Plus, Trash2 } from "lucide-react";
import { MobileSidebarToggle } from "./NextBase/MobileSidebarToggle";
import React, { forwardRef, Fragment } from "react";
import { hasProperty } from "@/utils";
import { AlertButton } from "./NextBase/AlertButton";

export type RightSectionOption = {
  identifier?: string;
} & (
  | ({
      label: string;
    } & Omit<ButtonProps, "children">)
  | ({
      optionType: "create";
      label: string;
      onPress: () => void;
    } & Omit<ButtonProps, "children">)
  | {
      optionType: "danger";
      label: string;
      onConfirm: () => Promise<void>;
      onCancel?: () => Promise<void>;
      isLoading?: boolean;
      content?: string | React.ReactNode;
      title?: string;
    }
  | {
      optionType: "custom";
      element: React.ReactNode;
    }
);

export type SubPageRightSection = {
  title?: string;
  contentClassName?: string;
  options: RightSectionOption[];
};

export type LimePageHeaderProps = {
  title: string | React.ReactNode;
  rightSection?: RightSectionOption;

  subPage?: {
    title: string;
    onBackButtonClick: () => void;
    rightSection?: SubPageRightSection | React.ReactNode;
  };
};

const subPageRightSectionIsCustomElement = (
  rightSection: SubPageRightSection | React.ReactNode,
): rightSection is React.ReactNode => {
  return !(rightSection as SubPageRightSection)?.options;
};

export const LimePageHeader = forwardRef<HTMLDivElement, LimePageHeaderProps>(
  ({ title, rightSection, subPage }, ref) => {
    const {
      isOpen: settingsOpened,
      onOpen: openSettings,
      onClose: closeSettings,
    } = useDisclosure();

    const isMobile = useMediaQuery("(max-width: 768px)");

    return (
      <>
        <div
          ref={ref}
          className={cn("w-full pt-0", {
            "h-[40px]": subPage != null && isMobile,
            "h-[60px]": subPage == null || isMobile,
          })}
        >
          <div className="border-divider relative flex h-full w-full items-center justify-between border-b">
            <div className="flex h-full w-full items-center gap-4 px-4 pb-0 md:items-center md:justify-between md:px-8 md:py-2 md:pb-1">
              {!subPage ? (
                <MobileSidebarToggle withoutRightMargin />
              ) : undefined}

              {subPage ? (
                <Button
                  variant="light"
                  isIconOnly
                  onPress={subPage.onBackButtonClick}
                >
                  <ArrowLeft size={16} />
                </Button>
              ) : undefined}

              {typeof title === "string" ? (
                <p className="mt-0 text-xl leading-6 font-semibold md:mt-2 md:text-lg">
                  {!subPage && title}
                </p>
              ) : (
                title
              )}
            </div>

            {subPage ? (
              <>
                <p className="pointer-events-none absolute w-full text-center text-sm leading-5 font-medium md:text-xl md:leading-6">
                  {subPage.title}
                </p>
              </>
            ) : undefined}

            <div className="mr-8">
              {buildSubPageRightSection({
                rightSection: subPage?.rightSection,
                settingsOpened,
                closeSettings,
                openSettings,
                isMobile: isMobile || false,
              }) ||
                (rightSection &&
                  buildOptionButton({
                    option: rightSection,
                    isMobile,
                    closeSettings,
                  }))}
            </div>
          </div>

          <Divider className="md:hidden" />
        </div>

        <Drawer
          isOpen={(isMobile || false) && settingsOpened}
          onClose={closeSettings}
          placement="bottom"
        >
          <DrawerContent>
            <DrawerHeader>
              {!subPageRightSectionIsCustomElement(subPage?.rightSection) &&
              subPage?.rightSection?.title
                ? subPage?.rightSection?.title
                : t`Nastavitve`}
            </DrawerHeader>
            <DrawerBody>
              {!subPageRightSectionIsCustomElement(subPage?.rightSection) &&
                subPage?.rightSection.options.map((option, index) => {
                  return (
                    <Fragment key={index}>
                      {buildOptionButton({
                        option,
                        isMobile,
                        isInDrawer: true,
                        closeSettings,
                      })}
                      {
                        // Don't render divider after last element
                        index !==
                        (subPage?.rightSection as SubPageRightSection).options
                          .length -
                          1 ? (
                          <Divider className="my-1" />
                        ) : undefined
                      }
                    </Fragment>
                  );
                })}
            </DrawerBody>
            <DrawerFooter></DrawerFooter>
          </DrawerContent>
        </Drawer>
      </>
    );
  },
);
LimePageHeader.displayName = "LimePageHeader";

const buildSubPageRightSection = ({
  rightSection,
  settingsOpened,
  closeSettings,
  openSettings,
  isMobile,
}: {
  rightSection: SubPageRightSection | React.ReactNode;
  settingsOpened: boolean;
  closeSettings: () => void;
  openSettings: () => void;
  isMobile: boolean;
}): React.ReactNode => {
  if (!rightSection) return undefined;

  if (subPageRightSectionIsCustomElement(rightSection)) {
    return rightSection;
  }

  const isCompactRightSection = (() => {
    if (
      rightSection.options.length === 1 &&
      hasProperty(rightSection.options[0], "optionType") &&
      rightSection.options[0].optionType === "create"
    ) {
      return false;
    }

    return true;
  })();

  return (
    <>
      {isMobile && (
        <div className="flex md:hidden">
          {isCompactRightSection && (
            <Button
              isIconOnly
              variant="light"
              onPress={() => openSettings()}
              data-identifier="right-section-three-dots"
            >
              <Ellipsis size={16} />
            </Button>
          )}

          {!isCompactRightSection &&
            buildOptionButton({
              option: rightSection.options[0],
              isMobile,
              closeSettings,
            })}
        </div>
      )}

      {!isMobile && (
        <div className="hidden md:block">
          {isCompactRightSection && (
            <Popover
              placement="bottom-end"
              showArrow
              isOpen={settingsOpened}
              key={rightSection.options[0].identifier}
              onOpenChange={(open) => {
                if (open) {
                  openSettings();
                } else {
                  closeSettings();
                }
              }}
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  variant="light"
                  className="hidden md:flex"
                  data-identifier="right-section-three-dots"
                >
                  <Ellipsis size={16} />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className={cn("px-1 py-1", rightSection.contentClassName)}
              >
                <div className="flex w-full flex-col gap-2">
                  {rightSection.title ? (
                    <p className="pt-1 text-sm leading-5 font-medium">
                      {rightSection.title}
                    </p>
                  ) : undefined}
                  {rightSection.options.map((option, index) => {
                    return (
                      <Fragment key={index}>
                        {buildOptionButton({
                          option,
                          isMobile,
                          closeSettings,
                        })}

                        {
                          // Don't render divider after last element
                          index !== rightSection.options.length - 1 ? (
                            <Divider />
                          ) : undefined
                        }
                      </Fragment>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {!isCompactRightSection &&
            buildOptionButton({
              option: rightSection.options[0],
              isMobile,
              closeSettings,
            })}
        </div>
      )}
    </>
  );
};

const buildOptionButton = ({
  option,
  isMobile,
  isInDrawer,
  closeSettings,
}: {
  option: SubPageRightSection["options"][number];
  isMobile?: boolean;
  isInDrawer?: boolean;
  closeSettings: () => void;
}) => {
  const optionType = hasProperty(option, "optionType")
    ? option.optionType
    : "default";

  const isLoading = hasProperty(option, "isLoading") ? option.isLoading : false;
  const onPress = hasProperty(option, "onPress") ? option.onPress : undefined;
  const label = hasProperty(option, "label") ? option.label : undefined;
  const identifier = hasProperty(option, "identifier")
    ? option.identifier
    : undefined;

  if (optionType === "danger") {
    const content = hasProperty(option, "content") ? option.content : undefined;
    const title = hasProperty(option, "title") ? option.title : undefined;
    const onConfirm = hasProperty(option, "onConfirm")
      ? option.onConfirm
      : undefined;
    const onCancel = hasProperty(option, "onCancel")
      ? option.onCancel
      : undefined;

    return (
      <AlertButton
        buttonProps={{
          color: "danger",
          variant: "flat",
          dataIdentifier: identifier,
          isLoading,
          startContent: <Trash2 size={16} />,
          type: "button",
          isIconOnly: isMobile && !isInDrawer,
        }}
        confirmProps={{
          onClick: async () => {
            await onConfirm?.();
            closeSettings();
          },
        }}
        cancelProps={{
          onClick: async () => {
            await onCancel?.();
            closeSettings();
          },
        }}
        content={content}
        title={title}
      >
        <span
          className={cn("hidden md:block", {
            block: isInDrawer,
          })}
        >
          {label}
        </span>
      </AlertButton>
    );
  }

  if (optionType === "create") {
    return (
      <Button
        color="primary"
        type="button"
        onPress={onPress}
        data-identifier={option.identifier}
        startContent={<Plus size={16} />}
        isIconOnly={isMobile && !isInDrawer}
        {...option}
        content={undefined}
      >
        <span
          className={cn("hidden md:block", {
            block: isInDrawer,
          })}
        >
          {label}
        </span>
      </Button>
    );
  }

  if (optionType === "custom") {
    return hasProperty(option, "element") ? option.element : undefined;
  }

  return (
    <Button
      variant="flat"
      type="button"
      color="primary"
      isIconOnly={isMobile && !isInDrawer}
      {...option}
      data-identifier={identifier}
      content={undefined}
    >
      {label}
    </Button>
  );
};
